import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, DataTable, GhostButton, PrimaryButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { CLASSES } from "@/lib/status";

const DIMS = ["R", "I", "A", "S", "E", "C", "OP", "CO", "EX", "AG", "ES"];

export default function QuestionBank() {
  const { data: matrix, reload } = useFetch("/admin/qbank/validation");
  const [cls, setCls] = useState("7");
  const [dim, setDim] = useState("R");
  const [q, setQ] = useState("");
  const { data: questions, loading, reload: reloadQ } = useFetch(`/admin/qbank/questions?class_level=${cls}&dimension=${dim}&q=${encodeURIComponent(q)}`);
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); reloadQ(); } catch (e) { toast.error(errMsg(e)); } };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    await run(async () => { const { data } = await api.post("/admin/qbank/import", fd); toast.message(`Imported: ${data.inserted} new, ${data.updated} updated`); }, "Question bank imported");
    setBusy(false);
    e.target.value = "";
  };

  return (
    <div data-testid="question-bank-page">
      <PageHeader eyebrow="Content" title="Question bank" description="Each class has its own pool across 11 dimensions. A class can only be active when every dimension has at least 18 active questions (9 per draw + reserve)."
        actions={<><PrimaryButton busy={busy} onClick={() => input.current?.click()} data-testid="qbank-import-button"><Upload className="h-4 w-4" />Import workbook (.xlsx)</PrimaryButton>
          <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="qbank-import-input" /></>} />
      <div className="card-surface overflow-x-auto" data-testid="qbank-validation-matrix">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#FAF8FB] border-b border-[#E9DFE7]"><th className="px-3 py-3 text-left">Class</th>{DIMS.map((d) => <th key={d} className="px-2 py-3 font-mono text-xs">{d}</th>)}<th className="px-3">Total</th><th className="px-3">Validation</th><th className="px-3">Active</th></tr></thead>
          <tbody>{matrix?.map((r) => (
            <tr key={r.class_level} className="border-b border-[#E9DFE7] last:border-0" data-testid={`qbank-row-${r.class_level}`}>
              <td className="px-3 py-3 font-semibold">Class {r.class_level}</td>
              {DIMS.map((d) => <td key={d} className={`px-2 text-center font-mono ${r.counts[d] < r.required_with_reserve ? "text-rose-700 font-bold" : ""}`}>{r.counts[d]}</td>)}
              <td className="px-3 text-center font-mono">{r.total}</td>
              <td className="px-3">{r.passed ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" />Pass</span> : <span className="text-rose-700 text-xs" title={r.shortages.join(", ")}><XCircle className="h-4 w-4 inline" /> Short: {r.shortages.join(", ")}</span>}</td>
              <td className="px-3"><Switch checked={r.active} disabled={!r.passed} onCheckedChange={(v) => run(() => api.post(`/admin/qbank/classes/${r.class_level}/active`, { active: v }), `Class ${r.class_level} ${v ? "activated" : "deactivated"}`)} data-testid={`qbank-active-switch-${r.class_level}`} /></td>
            </tr>))}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 mt-8 mb-4">
        <Select value={cls} onValueChange={setCls}><SelectTrigger className="w-36 bg-white" data-testid="qbank-class-filter"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{CLASSES.map((c) => <SelectItem key={c} value={String(c)}>Class {c}</SelectItem>)}</SelectContent></Select>
        <Select value={dim} onValueChange={setDim}><SelectTrigger className="w-36 bg-white" data-testid="qbank-dimension-filter"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{DIMS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
        <Input placeholder="Search text" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white" data-testid="qbank-search-input" />
      </div>
      <DataTable testId="qbank-questions-table" loading={loading} rows={questions} columns={[
        { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs">{r.id}</span> },
        { key: "text", label: "Question", className: "max-w-lg" },
        { key: "facet", label: "Facet" },
        { key: "exposure_count", label: "Used" },
        { key: "a", label: "", render: (r) => <GhostButton onClick={() => run(() => api.post(`/admin/qbank/questions/${r.id}/retire`, { retired: !r.retired }), r.retired ? "Question restored" : "Question retired")} data-testid={`qbank-retire-${r.id}`}>{r.retired ? "Restore" : "Retire"}</GhostButton> },
      ]} />
    </div>
  );
}
